import {
  ComponentProps,
  ComponentType,
  FC,
  PropsWithChildren,
  createContext,
  useContext,
} from "react";
import UIfx from "uifx";
import incomingMessage from "~@/assets/audio/incoming-message.mp3";
import newRoom from "~@/assets/audio/new-room.mp3";
import outgoingMessage from "~@/assets/audio/outgoing-message.mp3";

// Тип для контекста уведомлений: три звука для различных уведомлений
export type NotificationsContext = {
  outgoingMessage: UIfx;
  incomingMessage: UIfx;
  newRoom: UIfx;
};

// Создание контекста уведомлений, изначально undefined
// eslint-disable-next-line @typescript-eslint/no-redeclare -- companion object
export const NotificationsContext = createContext<
  NotificationsContext | undefined
>(undefined);

// Хук для использования NotificationsContext в компонентах
export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);

  // Выбрасывает ошибку, если контекст не был найден
  if (!context) {
    throw new Error("Missing NotificationsProvider.");
  }

  return context;
};

// Провайдер для NotificationsContext, создающий экземпляры UIfx для уведомлений
export const NotificationsProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  return (
    <NotificationsContext.Provider
      value={{
        outgoingMessage: new UIfx(outgoingMessage, {
          volume: 1,
          throttleMs: 500,
        }),
        incomingMessage: new UIfx(incomingMessage, {
          volume: 1,
          throttleMs: 500,
        }),
        newRoom: new UIfx(newRoom, {
          volume: 1,
          throttleMs: 500,
        }),
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

// Функция высшего порядка для оборачивания компонента в NotificationsProvider
export const withNotificationsProvider = <P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> => {
  return function WithNotificationProvider(
    props: ComponentProps<typeof WrappedComponent>
  ) {
    return (
      <NotificationsProvider>
        <WrappedComponent {...props} />
      </NotificationsProvider>
    );
  };
};
